import Vue from 'vue';
import {Component, Emit, Prop, Watch} from 'vue-property-decorator';
import {Validations} from 'vuelidate-property-decorators';
import ToolService from '@/providers/tools';

@Component
class EdTextField extends Vue {
  @Prop({default: 'text'}) readonly type!: string;
  @Prop({default: '18px'}) readonly suffixIconFontSize!: string;
  @Prop({default: null}) readonly paddingInput!: string;
  @Prop({default: 'var(--ed-color-light-contrast)'}) readonly inputTextColorInactive!: string;
  @Prop({default: 'var(--ed-color-light-contrast)'}) readonly inputBorderBottomColorInactive!: string;
  @Prop({default: ''}) readonly inputPlaceholderColorInactive!: string;
  @Prop({default: ' '}) readonly placeholder!: string;
  @Prop({type: Boolean, default: false}) readonly onlyNumbers!: boolean;
  @Prop({type: Boolean, default: false}) readonly readonly!: boolean;
  @Prop({type: Boolean, default: false}) readonly disabled!: boolean;
  @Prop({type: Boolean, default: false}) readonly clearable!: boolean;
  @Prop({type: Boolean, default: false}) readonly labelFloating!: boolean;
  @Prop({type: Boolean, default: false}) readonly filled!: boolean;
  @Prop({type: Boolean, default: false}) readonly singleLine!: boolean;
  @Prop({type: Boolean, default: false}) readonly positiveNumeric!: boolean;
  @Prop({type: Boolean, default: false}) readonly phoneNumber!: boolean;
  @Prop({type: Boolean, default: false}) readonly phoneExt!: boolean;
  @Prop({type: Boolean, default: false}) readonly required: boolean;
  @Prop({type: Boolean, default: false}) readonly inputTextCenter: boolean;
  @Prop({type: Boolean, default: false}) readonly numberGreaterThanZero!: boolean;
  @Prop({type: String, default: 'reset-text-field-validation'}) readonly resetEvent!: string;
  @Prop({type: Number, default: 0.6}) readonly placeholderOpacity!: number;
  @Prop() readonly label!: string;
  @Prop() readonly counter!: number;
  @Prop() readonly maxlength: number;
  @Prop({type: Boolean, default: false}) readonly error!: boolean;
  @Prop({type: Boolean, default: false}) readonly forceError!: boolean;
  @Prop({type: Array, default: () => []}) readonly messages!: string[];
  @Prop({type: Array, default: () => []}) readonly errorMessages!: string[];
  @Prop() readonly maxNumber!: number;
  @Prop() readonly minNumber!: number;
  @Prop({ default: false }) readonly showDefaultSpin!: boolean;

  value: string = '';
  private paddingInputLocal: string = '5px 0px 9px 0';
  isFocused: boolean = false;
  private isValidText: boolean = false;
  private previousValue: string = '';
  private compositionText: string = '';
  private isComposing: boolean = false;

  @Validations()
  validations() {
    const attrs: any = this.$attrs;
    return {
      value: attrs?.value?.validators ?? {}
    };
  }
  get valueLength(){
    return this.value ? this.value.length : 0;
  }
  get configStyles() {
    return {
      '--ed-indicator-color': this.isInvalid ? 'var(--ed-color-danger)' : 'var(--ed-color-green)',
      '--ed-counter-color': this.isInvalid ? 'var(--ed-color-danger)' : 'var(--ed-color-medium)',
      '--ed-border-color-active': this.borderColor,
      '--ed-message-support-color': this.messageSupportColor,
      '--ed-border-color-hover': this.borderColorHover,
      '--ed-input-text-center': this.inputTextCenter ? 'center' : 'none',
      '--ed-suffixIcon-font-size': this.suffixIconFontSize,
      '--ed-icon-prepend-suffix-icon': this.inputTextAndIconColor,
      '--ed-placeholder-opacity': this.placeholderOpacity,
      '--ed-placeholder-color': this.inputPlaceholderColorInactive,
      '--ed-padding-prepend-suffix-icon': this.paddingFormControl,
    };
  }

  get messageSupportColor() {
    if (this.forceError) {
      return 'var(--ed-color-danger)';
    }
    return this.isFocused ? 'var(--ed-color-green)' 
      : 'rgba(0, 0, 0, 0.6)';
  }

  get inputTextAndIconColor() {
    if (this.forceError) {
      return 'var(--ed-color-danger)';
    }
    return this.isInvalid && this.isFocused ? 'var(--ed-color-danger)'
      : this.isFocused ? 'var(--ed-color-green)' : this.inputTextColorInactive;
  }

  get borderColor() {
    if (this.forceError) {
      return 'var(--ed-color-danger)';
    }
    return this.isInvalid && this.isFocused ? 'var(--ed-color-danger)' : this.isFocused ? 'var(--ed-color-green)' : this.inputBorderBottomColorInactive;
  }

  get borderColorHover() {
    if (this.forceError) {
      return 'var(--ed-color-danger)';
    }
    return this.isFocused && this.errorMessagesList.length > 0 ? 'var(--ed-color-danger)'
      : !this.isFocused && this.errorMessagesList.length > 0 ? 'var(--ed-color-light-contrast)' : 'rgba(0, 0, 0, 0.8)';
  }

  get isLabelFloatingActive(): boolean {
    if (this.labelFloating) {
      return this.isFocused || this.value?.length > 0;
    }

    return false;
  }

  get isPlaceholderActive(): boolean {
    if (this.singleLine) {
      return this.isFocused || this.value?.length > 0;
    }

    return false;
  }

  get isLabelFilledActive(): boolean {
    if (this.filled) {
      return this.isFocused || this.value?.length > 0;
    }

    return false;
  }

  get isClearableActive(): boolean {
    return this.isFocused && this.clearable;
  }

  get hasSuffixContent() {
    return !!this.$slots.suffix;
  }

  get errorMessagesList(): string[] {
    const messages = [];

    if (this.errorMessages && this.errorMessages.length > 0) {
      messages.push(...this.errorMessages);
    }
    
    if (this.isRequiredError) {
      messages.push(this.$i18n.t('errors.required'));
    }
    if (this.isMaxLengthError) {
      messages.push(this.$i18n.t('errors.maxLengthData', {max: this.$v.value.$params.maxLength.max}));
    }
    if (this.isMinLengthError) {
      messages.push(this.$i18n.t('errors.minLengthData', {min: this.$v.value.$params.minLength.min}));
    }
    if (this.isUrlError) {
      messages.push(this.$i18n.t('errors.url'));
    }
    if (this.isPositiveNumberError || this.isValidOnlyNumber) {
      if (messages.length > 0) {
        messages.pop();
        messages.push(this.$i18n.t('errors.number'));
      } else {
        messages.push(this.$i18n.t('errors.number'));
      }
    }
    return messages;
  }

  get isInvalid() {
    return this.error || (this.isValidText && this.$v.value.$invalid) || this.isValidOnlyNumber;
  }

  get isMaxLengthError() {
    if (this.$v.value.$params.maxLength && this.isInvalid) {
      return !this.$v.value.maxLength;
    }
    return false;
  }

  get isMinLengthError() {
    if (this.$v.value.$params.minLength && this.isInvalid) {
      return !this.$v.value.minLength;
    }
    return false;
  }

  get isUrlError() {
    if (this.$v.value.$params.url && this.isInvalid) {
      return !this.$v.value.url;
    }
    return false;
  }

  get isPositiveNumberError() {
    if (this.$v.value.$params.numeric && this.isInvalid && this.positiveNumeric) {
      return !this.$v.value.numeric;
    }
    return false;
  }

  get isRequiredError() {
    if (this.$v.value.$params.required && this.isInvalid) {
      return !this.$v.value.required;
    }
    return false;
  }

  get isValidOnlyNumber() {
    return this.onlyNumbers && this.isComposing;
  }

  clearValue() {
    this.value = '';
    this.setValue();
  }

  onCompositionStart(event: CompositionEvent) {
    if (this.onlyNumbers) {
      this.isComposing = true;
      this.compositionText = '';
      this.errorMessagesList;
    }
  }

  onCompositionUpdate(event: CompositionEvent) {
    const inputElement = event.target as HTMLInputElement;
    const cursorPosition = inputElement.selectionStart;

    if (cursorPosition !== null) {
      const textBeforeCursor = inputElement.value.slice(0, cursorPosition);
      const textAfterCursor = inputElement.value.slice(cursorPosition);
      this.compositionText = textBeforeCursor + event.data + textAfterCursor;
      this.value = this.compositionText;
    }
  }

  onCompositionEnd(event: CompositionEvent) {
    if (this.isValidOnlyNumber) {
      this.isComposing = false;
      this.value = this.compositionText;
    }
  }

  @Emit('keydown')
  keydown(event) {
    const isNavigationOrDeletionKey = event.code === 'Backspace' ||
      event.code === 'Delete' ||
      event.code?.startsWith('Arrow');

    const isSelectAll = event.key?.toLowerCase() === 'a' && (event.ctrlKey || event.metaKey);

    if (isNavigationOrDeletionKey || isSelectAll) {
      return;
    }

    if (this.value?.length === this.maxlength) {
      event.preventDefault();
    }

    if (this.onlyNumbers) {
      const value = parseFloat(event.key);

      if (isNaN(value)) {
        const regexExt = /[^0-9]/g;
        const processedValue = this.value.replace(regexExt, '');
        if (this.value !== processedValue) {
          this.value = processedValue;
        }
        event.preventDefault();
      }

      if ((event.key === '-' && this.value?.length > 0) ||
        (this.value?.length === 0 && value === 0)) {
        event.preventDefault();
      }
    }
  }

  @Emit('keyup')
  onKeyUp(event): void {
    const value = parseFloat(event.key);
  }

  onInput(event: InputEvent) {
    if (this.phoneExt || this.onlyNumbers) {
      const regexExt = /[^0-9]/g;
      const processedValue = this.value.replace(regexExt, '');
      if (this.value !== processedValue) {
        this.value = processedValue;
      }
    }

    if (this.phoneNumber || this.onlyNumbers) {
      const regexTest = /^\+?[0-9]*$/;
      const regex = /[^0-9]/g;
      if (!regexTest.test(this.value)) {
        if (this.value.charAt(0) === '+') {
          const processedValue = `+${this.value.replace(regex, '')}`;
          if (this.value !== processedValue) {
            this.value = processedValue;
          }
        } else {
          const processedValue = this.value.replace(regex, '');
          if (this.value !== processedValue) {
            this.value = processedValue;
          }
        }
      }
    }

    const inputRef = this.$refs.inputRef as HTMLInputElement;
    if (event?.data == '.' && this.type == 'number' && !this.value.includes('.')) {
      inputRef.value = inputRef.value.concat('.');
    }

    let isDeleting = event.inputType === 'deleteContentBackward';

    const previousValueNumber = parseFloat(this.previousValue);
    const currentValueNumber = parseFloat(this.value);

    let notAllowInsertZero = ((previousValueNumber > currentValueNumber && this.value === '0') || this.value[0] === '0');
    if (!isDeleting && notAllowInsertZero && this.numberGreaterThanZero) {
      this.value = this.previousValue;
    }

    this.setValue();
  }

  onBeforeInput(event: InputEvent) {
    this.previousValue = this.value;
  }

  setValue() {
    this.$emit('input', {
      value: this.value,
      valid: !this.$v.value.$invalid && !this.isValidOnlyNumber,
      validators: this.validators
    });
  }

  get validators() {
    let input: any = this.$attrs.value;
    return input ? input.validators : {};
  }

  get paddingFormControl() {
    return this.paddingInputLocal;
  }

  private buildPaddingSuffix() {
    if (!this.hasSuffixContent) {
      return;
    }

    const suffixIcons = this.$refs.prependSuffixIcon as HTMLElement | null;

    if (suffixIcons) {
      const suffixWidth = suffixIcons.offsetWidth;
      this.paddingInputLocal = this.paddingInput ?? `5px ${suffixWidth + 8}px 9px 0`;
      return;
    }

    this.paddingInputLocal = '5px 0px 9px 0';
  }

  setBlur(): void {
    setTimeout(() => {
      this.isFocused = false;
    }, 100);
    this.$emit('blur');
  }

  setFocus(): void {
    this.isFocused = true;
    this.$emit('focus');
  }

  keyUpEnter(): void {
    this.$emit('enter');
  }

  created() {
    const input = this.$attrs.value as any;

    if (input) {
      input.validators = input.validators ?? {};
      this.value = input.value;
      this.setValue();
    }
  }

  resetValidationCustom() {
    if (this.$v) {
      this.$v.$reset();
      this.isValidText = false;
    }
  }

  mounted() {
    this.$root.$on(this.resetEvent, this.resetValidationCustom);
    this.buildPaddingSuffix();
  }

  beforeDestroy() {
    this.$root.$off(this.resetEvent, this.resetValidationCustom);
  }

  @Watch('value')
  resetValidation() {
    this.isValidText = !this.$v.value.maxLength || !this.$v.value.url;
  }

  @Watch('isFocused')
  focusValidation() {
    if (!this.isFocused) {
      this.isValidText = this.$v.value.$invalid;
    }
  }

  @Watch('$attrs')
  attrsChanged() {
    let input: any = this.$attrs.value;
    let newValue = input?.value ?? '';
    const processedValue = ToolService.exitSpecialChars(newValue);

    if (this.value !== processedValue) {
      this.value = processedValue;
      this.setValue();
    }

  }
}

export default Vue.component('ed-text-field', EdTextField);